import { enqueue, clearQueue, processQueue } from '@core/api/queue';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import localforage from 'localforage';
import { useEffect, useState, useRef } from 'react';

/**
 * Workaround for caching blobs for google's signed urls.
 * TODO [https://gitlab.com/deepup-gmbh/team-backoffice/-/issues/19]: Remove as soon as signed cookie mechanism exists
 */

const QUEUE_KEY = 'cashed-url-queue';

export const useCachedUrl = (
  url?: string,
  omitCache?: boolean,
  requestConfig?: () => Promise<AxiosRequestConfig>,
): string | undefined => {
  const [cachedUrl, setCachedUrl] = useState<string>();

  // Prevent dynamic requestConfig references from triggering effect loops
  const requestConfigRef = useRef(requestConfig);
  useEffect(() => {
    requestConfigRef.current = requestConfig;
  }, [requestConfig]);

  useEffect(() => {
    if (!url || omitCache) return;

    const simplifiedPath = url.split('?')[0];
    let active = true;
    let urlToRevoke: string | null = null;

    const processRequest = (buffer: ArrayBuffer) => {
      if (!active) return;
      const blob = new Blob([buffer]);
      urlToRevoke = URL.createObjectURL(blob);
      setCachedUrl(urlToRevoke);
    };

    const fetchData = async () => {
      try {
        const config: AxiosRequestConfig = {
          responseType: 'arraybuffer',
          ...(requestConfigRef.current ? await requestConfigRef.current() : {}),
        };
        const response: AxiosResponse<ArrayBuffer> = await axios.get(url, config);
        if (!active) return;
        await localforage.setItem(simplifiedPath, response.data);
        processRequest(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    const loadData = async () => {
      const cache = (await localforage.getItem(simplifiedPath)) as ArrayBuffer;
      if (!active) return;
      if (cache && cache instanceof ArrayBuffer) {
        processRequest(cache);
        return;
      }

      enqueue(QUEUE_KEY, fetchData);
      processQueue(QUEUE_KEY);
    };

    loadData();

    return () => {
      active = false;
      clearQueue(QUEUE_KEY);
      if (urlToRevoke) {
        URL.revokeObjectURL(urlToRevoke);
      }
    };
  }, [url, omitCache]);

  return omitCache ? url : cachedUrl;
};
